import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import HydrantyService from "./HydrantyService";
import { QueryError } from "../../QueryError";
import { NotFoundError } from "../../NotFound";
import { isMockActive } from "../../../utils/isMockActive";
import type {
    HydrantyGeoJSON,
    HydrantyInfoResponse,
    HydrantyUploadRequest,
    HydrantyGpxUploadRequest,
    HydrantyUploadResponse,
} from "../generated";

// -----------------------------
// QUERIES
// -----------------------------

export const useHydrantyQuery = (enabled = true) => {
    const effectiveEnabled = enabled && !isMockActive();
    return useQuery<HydrantyGeoJSON | null, Error>({
        queryKey: ["hydranty"],
        queryFn: async () => {
            try {
                // Fetch from API
                const data = await HydrantyService.getHydranty();
                HydrantyService.setCachedHydranty(data);
                return data;
            } catch (error: unknown) {
                // Handle 404 gracefully - no hydrant data available
                if (error instanceof NotFoundError) {
                    return null;
                }
                if (error instanceof QueryError && error.statusCode === 404) {
                    return null;
                }
                throw error;
            }
        },
        enabled: effectiveEnabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: (failureCount, error: Error) => {
            // Don't retry on 404
            if (error instanceof NotFoundError) return false;
            if (error instanceof QueryError && error.statusCode === 404) return false;
            return failureCount < 1;
        },
    });
};

export const useHydrantyInfoQuery = () => {
    return useQuery<HydrantyInfoResponse, Error>({
        queryKey: ["hydrantyInfo"],
        queryFn: () => HydrantyService.getHydrantyInfo(),
        retry: (failureCount, error: Error) => {
            // Don't retry on 404
            if (error instanceof NotFoundError) return false;
            if (error instanceof QueryError && error.statusCode === 404) return false;
            return failureCount < 1;
        },
    });
};

// -----------------------------
// MUTATIONS
// -----------------------------

export const useUploadHydrantyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<HydrantyUploadResponse, Error, HydrantyUploadRequest>({
        mutationFn: (data) => HydrantyService.uploadHydranty(data),

        onSuccess: (response) => {
            // Invalidate cache and queries
            HydrantyService.invalidateHydrantyCache();
            queryClient.invalidateQueries({ queryKey: ["hydranty"] });
            queryClient.invalidateQueries({ queryKey: ["hydrantyInfo"] });
            showToast(response.message || "Hydranty byly úspěšně nahrány.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nahrát hydranty.", "error");
        },
    });
};

export const useUploadHydrantyGpxMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<HydrantyUploadResponse, Error, HydrantyGpxUploadRequest>({
        mutationFn: (data) => HydrantyService.uploadHydrantyGpx(data),

        onSuccess: (response) => {
            // Invalidate cache and queries
            HydrantyService.invalidateHydrantyCache();
            queryClient.invalidateQueries({ queryKey: ["hydranty"] });
            queryClient.invalidateQueries({ queryKey: ["hydrantyInfo"] });
            showToast(response.message || "GPX hydranty byly úspěšně nahrány.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nahrát GPX hydranty.", "error");
        },
    });
};

export const useDeleteHydrantyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: () => HydrantyService.deleteHydranty(),

        onSuccess: () => {
            // Invalidate cache and queries
            HydrantyService.invalidateHydrantyCache();
            queryClient.invalidateQueries({ queryKey: ["hydranty"] });
            queryClient.invalidateQueries({ queryKey: ["hydrantyInfo"] });
            showToast("Hydranty byly úspěšně smazány.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat hydranty.", "error");
        },
    });
};
